import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { Activity, Save, Droplet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FDI-Reihenfolge (Ober-/Unterkiefer)
const FDI = [
  '18','17','16','15','14','13','12','11','21','22','23','24','25','26','27','28',
  '48','47','46','45','44','43','42','41','31','32','33','34','35','36','37','38',
];

const emptyTooth = () => ({ pd: ['', '', '', '', '', ''], bop: [false, false, false, false, false, false], plaque: false });

const DentalPerioPage = () => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [teeth, setTeeth] = useState({});
  const [note, setNote] = useState('');
  const [trend, setTrend] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.customers || []))).catch(() => {});
  }, []);

  const load = useCallback(async (id) => {
    if (!id) return;
    const init = {};
    FDI.forEach((f) => { init[f] = emptyTooth(); });
    try {
      const [h, latest] = await Promise.all([
        axios.get(`${API}/dental/perio/${id}/history`),
        axios.get(`${API}/dental/perio/${id}/latest`),
      ]);
      setTrend(h.data?.trend || []);
      const sess = latest.data?.session;
      if (sess?.teeth) {
        Object.entries(sess.teeth).forEach(([fdi, t]) => {
          if (!FDI.includes(fdi)) return;
          const pd = ['', '', '', '', '', ''];
          (t.pd || []).slice(0, 6).forEach((v, i) => { pd[i] = String(v); });
          const bop = ['', '', '', '', '', ''].map((_, i) => !!(t.bop || [])[i]);
          init[fdi] = { pd, bop, plaque: !!t.plaque };
        });
      }
    } catch { /* noop */ }
    setTeeth(init);
    setNote('');
  }, []);

  useEffect(() => { if (customerId) load(customerId); }, [customerId, load]);

  const setPd = (fdi, idx, val) => {
    setTeeth((prev) => {
      const t = { ...(prev[fdi] || emptyTooth()) };
      const pd = [...t.pd]; pd[idx] = val.replace(/[^0-9]/g, '').slice(0, 2); t.pd = pd;
      return { ...prev, [fdi]: t };
    });
  };
  const toggleBop = (fdi, idx) => {
    setTeeth((prev) => {
      const t = { ...(prev[fdi] || emptyTooth()) };
      const bop = [...t.bop]; bop[idx] = !bop[idx]; t.bop = bop;
      return { ...prev, [fdi]: t };
    });
  };
  const togglePlaque = (fdi) => {
    setTeeth((prev) => {
      const t = { ...(prev[fdi] || emptyTooth()) };
      t.plaque = !t.plaque;
      return { ...prev, [fdi]: t };
    });
  };

  // Live-Kennzahlen (Spiegel der Server-Berechnung)
  const agg = useMemo(() => {
    const pdVals = []; let bopTot = 0, bopPos = 0, over4 = 0, over6 = 0, plaqueTeeth = 0, toothCount = 0;
    Object.values(teeth).forEach((t) => {
      const hasData = (t.pd || []).some((v) => v !== '') || (t.bop || []).some(Boolean) || t.plaque;
      if (!hasData) return;
      toothCount += 1;
      (t.pd || []).forEach((v) => { if (v !== '') { const mm = +v; pdVals.push(mm); if (mm >= 4) over4 += 1; if (mm >= 6) over6 += 1; } });
      (t.bop || []).forEach((b) => { bopTot += 1; if (b) bopPos += 1; });
      if (t.plaque) plaqueTeeth += 1;
    });
    return {
      mean_pd: pdVals.length ? (pdVals.reduce((a, b) => a + b, 0) / pdVals.length).toFixed(2) : '0.00',
      max_pd: pdVals.length ? Math.max(...pdVals) : 0,
      bop_percent: bopTot ? (100 * bopPos / bopTot).toFixed(1) : '0.0',
      plaque_percent: toothCount ? (100 * plaqueTeeth / toothCount).toFixed(1) : '0.0',
      sites_over_4mm: over4, sites_over_6mm: over6,
    };
  }, [teeth]);

  const save = async () => {
    if (!customerId) { toast.error('Bitte Patient wählen'); return; }
    setSaving(true);
    try {
      const payload = { teeth: {}, note };
      Object.entries(teeth).forEach(([fdi, t]) => {
        payload.teeth[fdi] = { pd: (t.pd || []).filter((v) => v !== '').map(Number), bop: t.bop, plaque: t.plaque };
      });
      await axios.post(`${API}/dental/perio/${customerId}`, payload);
      toast.success('Parodontal-Befund gespeichert');
      load(customerId);
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const renderRow = (fdi) => {
    const t = teeth[fdi] || emptyTooth();
    return (
      <tr key={fdi} className="border-b border-slate-100" data-testid={`perio-row-${fdi}`}>
        <td className="px-2 py-1 font-bold text-slate-700 text-center bg-slate-50">{fdi}</td>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <td key={i} className="px-0.5 py-1">
            <input
              value={t.pd[i]}
              onChange={(e) => setPd(fdi, i, e.target.value)}
              className="w-9 h-8 text-center text-sm border border-slate-200 rounded focus:border-teal-500 outline-none"
              data-testid={`perio-pd-${fdi}-${i}`}
            />
          </td>
        ))}
        <td className="px-1">
          <div className="flex gap-0.5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <button key={i} onClick={() => toggleBop(fdi, i)} title="Blutung (BOP)"
                className={`w-4 h-4 rounded-full border ${t.bop[i] ? 'bg-red-500 border-red-500' : 'bg-white border-slate-300'}`}
                data-testid={`perio-bop-${fdi}-${i}`} />
            ))}
          </div>
        </td>
        <td className="px-2 text-center">
          <input type="checkbox" checked={t.plaque} onChange={() => togglePlaque(fdi)} data-testid={`perio-plaque-${fdi}`} />
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="dental-perio-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Activity className="text-teal-600" size={24} /> Parodontal-Befund</h1>
        <p className="text-sm text-slate-500">Taschentiefen, Blutung (BOP) und Plaque je Zahn – mit Verlauf über die Sitzungen.</p>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="min-w-[260px]"><CustomerSearchSelect customers={customers} value={customerId} onValueChange={setCustomerId} placeholder="Patient suchen..." /></div>
          {customerId && (
            <Button onClick={save} disabled={saving} className="ml-auto" data-testid="perio-save-btn"><Save size={16} className="mr-1" /> Befund speichern</Button>
          )}
        </CardContent>
      </Card>

      {customerId && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card data-testid="perio-agg-meanpd"><CardContent className="p-3"><div className="text-2xl font-bold text-slate-800">{agg.mean_pd}</div><div className="text-xs text-slate-500">Ø Taschentiefe (mm)</div></CardContent></Card>
            <Card data-testid="perio-agg-bop"><CardContent className="p-3"><div className="text-2xl font-bold text-red-600">{agg.bop_percent}%</div><div className="text-xs text-slate-500">BOP (Blutung)</div></CardContent></Card>
            <Card data-testid="perio-agg-plaque"><CardContent className="p-3"><div className="text-2xl font-bold text-amber-600">{agg.plaque_percent}%</div><div className="text-xs text-slate-500">Plaque</div></CardContent></Card>
            <Card data-testid="perio-agg-over4"><CardContent className="p-3"><div className="text-2xl font-bold text-orange-600">{agg.sites_over_4mm}</div><div className="text-xs text-slate-500">Stellen ≥ 4 mm</div></CardContent></Card>
            <Card data-testid="perio-agg-over6"><CardContent className="p-3"><div className="text-2xl font-bold text-red-700">{agg.sites_over_6mm}</div><div className="text-xs text-slate-500">Stellen ≥ 6 mm</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Droplet size={16} className="text-red-500" /> Befundblatt (6 Messpunkte je Zahn)</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500">
                    <th className="px-2 py-1">Zahn</th>
                    <th colSpan={6} className="px-2 py-1">Taschentiefen (mm)</th>
                    <th className="px-1 py-1">Blutung</th>
                    <th className="px-2 py-1">Plaque</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan={9} className="px-2 py-1 text-xs font-semibold text-teal-700 bg-teal-50">Oberkiefer</td></tr>
                  {FDI.slice(0, 16).map(renderRow)}
                  <tr><td colSpan={9} className="px-2 py-1 text-xs font-semibold text-teal-700 bg-teal-50">Unterkiefer</td></tr>
                  {FDI.slice(16, 32).map(renderRow)}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Notiz</CardTitle></CardHeader>
            <CardContent><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bemerkungen zur Sitzung…" data-testid="perio-note" /></CardContent>
          </Card>

          {trend.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Verlauf</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-slate-500 border-b"><th className="px-2 py-1 text-left">Datum</th><th className="px-2 py-1">Ø PD</th><th className="px-2 py-1">BOP %</th><th className="px-2 py-1">Plaque %</th><th className="px-2 py-1">≥4mm</th><th className="px-2 py-1">≥6mm</th></tr></thead>
                  <tbody>
                    {trend.map((s, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`perio-trend-${i}`}>
                        <td className="px-2 py-1">{(s.created_at || '').slice(0, 10)}</td>
                        <td className="px-2 py-1 text-center">{s.mean_pd}</td>
                        <td className="px-2 py-1 text-center">{s.bop_percent}%</td>
                        <td className="px-2 py-1 text-center">{s.plaque_percent}%</td>
                        <td className="px-2 py-1 text-center">{s.sites_over_4mm}</td>
                        <td className="px-2 py-1 text-center">{s.sites_over_6mm}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DentalPerioPage;
